import Phaser from 'phaser';
import { BIG_THINGS, type BigThing } from '../data/bigThings';
import { markUnlocked } from '../state/milestones';

interface MilestoneSceneData {
  bigThingId: string;
  /** True when re-opening an already-unlocked monument — skips confetti
   *  and skips the onMilestoneClosed callback so we don't re-fire the
   *  unlock animation or the final ceremony. */
  replay?: boolean;
}

export class MilestoneScene extends Phaser.Scene {
  private big!: BigThing;
  private replay = false;

  constructor() {
    super({ key: 'Milestone', active: false });
  }

  init(data: MilestoneSceneData) {
    const found = BIG_THINGS.find((bt) => bt.id === data.bigThingId);
    if (!found) {
      this.scene.stop();
      return;
    }
    this.big = found;
    this.replay = !!data.replay;
  }

  create() {
    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const narrow = W < 1100;

    if (!this.replay) markUnlocked(this.big.id);

    // Hide ContinentScene chrome behind us so we own the screen.
    const continent = this.scene.get('Continent') as Phaser.Scene & {
      setChromeVisible?: (v: boolean) => void;
    };
    continent?.setChromeVisible?.(false);
    this.events.once('shutdown', () => continent?.setChromeVisible?.(true));

    // Soft warm vignette backdrop — keeps the map ghosted behind so the
    // unlock feels rooted in place rather than a foreign overlay.
    this.add.rectangle(W / 2, H / 2, W, H, 0x0a0806, 0.92).setInteractive();

    // Eyebrow — different copy depending on whether this is a fresh unlock
    // or the player tapping back in to admire what they've already earned.
    const eyebrowY = narrow ? 100 : 80;
    const eyebrowText = this.replay
      ? 'M O N U M E N T'
      : 'M I L E S T O N E   |   M O N U M E N T   U N L O C K E D';
    this.add.text(W / 2, eyebrowY, eyebrowText, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '20px' : '15px',
      color: '#b19b6e',
    }).setOrigin(0.5);

    // Headline
    const headlineY = eyebrowY + (narrow ? 50 : 36);
    const headlineText = this.replay ? 'From the shelf' : "You've unearthed";
    this.add.text(W / 2, headlineY, headlineText, {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '34px' : '34px',
      color: '#ead5ac', fontStyle: 'italic',
    }).setOrigin(0.5);

    // Big Thing sprite — hero element. Targets a fixed display height.
    const heroH = narrow ? 320 : 260;
    const spriteY = Math.round(H * 0.46);
    const spriteKey = `bigthing-${this.big.id}`;
    let sprite: Phaser.GameObjects.GameObject;
    if (this.textures.exists(spriteKey) && this.textures.get(spriteKey).source[0]?.width > 1) {
      const img = this.add.image(W / 2, spriteY, spriteKey);
      const nativeH = img.height;
      img.setScale(heroH / nativeH);
      img.setAlpha(0);
      this.tweens.add({
        targets: img, alpha: 1, scale: { from: heroH / nativeH * 0.6, to: heroH / nativeH },
        duration: 700, ease: 'Back.Out', delay: 200,
      });
      sprite = img;
    } else {
      // Fallback if PNG not yet baked — render a "?" placeholder
      sprite = this.add.text(W / 2, spriteY, '?', {
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: `${narrow ? 200 : 160}px`,
        color: '#5a4e3a',
      }).setOrigin(0.5);
    }

    // Label
    const labelY = spriteY + heroH / 2 + (narrow ? 40 : 30);
    this.add.text(W / 2, labelY, this.big.label, {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '36px' : '36px',
      color: '#ffd966', fontStyle: 'italic',
    }).setOrigin(0.5, 0);

    // Location
    const locY = labelY + (narrow ? 50 : 38);
    this.add.text(W / 2, locY, this.big.location, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '22px' : '17px',
      color: '#b19b6e',
    }).setOrigin(0.5, 0);

    // Blurb
    const blurbY = locY + (narrow ? 36 : 26);
    this.add.text(W / 2, blurbY, this.big.blurb, {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '20px' : '21px',
      color: '#ead5ac', fontStyle: 'italic',
      align: 'center', wordWrap: { width: Math.min(W - 80, 560) },
    }).setOrigin(0.5, 0);

    // Continue button
    const btnY = narrow ? H - 110 : H - 80;
    const btn = this.add.text(W / 2, btnY, '▸  back to the map  ▸', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '20px' : '21px',
      color: '#0d0a08', backgroundColor: '#ffd966',
      padding: { x: narrow ? 24 : 18, y: narrow ? 14 : 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => btn.setBackgroundColor('#fff5c0'));
    btn.on('pointerout', () => btn.setBackgroundColor('#ffd966'));
    btn.on('pointerup', () => {
      const c = this.scene.get('Continent') as Phaser.Scene & {
        onMilestoneClosed?: (id: string) => void;
      };
      this.scene.stop();
      // Replay mode is a passive view — don't re-fire the unlock animation
      // or the final ceremony.
      if (!this.replay) c?.onMilestoneClosed?.(this.big.id);
    });

    // Confetti only on the fresh unlock — re-opens are passive.
    if (!this.replay) this.spawnConfetti(W / 2, spriteY);
    sprite; // silence unused
  }

  private spawnConfetti(cx: number, cy: number) {
    const colours = [0xffd966, 0xfff5c0, 0xa83320, 0x88ff66, 0xffe78a, 0x4a8db8];
    for (let i = 0; i < 70; i++) {
      const colour = colours[i % colours.length];
      const r = this.add.rectangle(cx, cy, 5, 5, colour);
      r.setDepth(200);
      const angle = Math.random() * Math.PI * 2;
      const speed = Phaser.Math.Between(120, 320);
      const dx = Math.cos(angle) * speed;
      const dy = Math.sin(angle) * speed - 80;
      this.tweens.add({
        targets: r,
        x: cx + dx, y: cy + dy,
        alpha: { from: 1, to: 0 },
        angle: Phaser.Math.Between(-360, 360),
        duration: Phaser.Math.Between(800, 1500),
        ease: 'Sine.Out',
        delay: Phaser.Math.Between(0, 400),
        onComplete: () => r.destroy(),
      });
    }
  }
}
